import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { ContentService } from '../content.service';

export interface ContentSearchResult {
  id: string;
  title: string;
  type: string;
  name: string;
}

@Component({
  selector: 'app-content-searcher',
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatFormFieldModule, MatInputModule, TranslateModule],
  templateUrl: './content-searcher.component.html',
  styleUrls: ['./content-searcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentSearcherComponent {
  private contentService = inject(ContentService);
  private translate = inject(TranslateService);

  /** Emits the selected item */
  itemSelected = output<ContentSearchResult>();
  /** Emits when the popover should close */
  closed = output<void>();

  /**
   * 'attach' = shows a '+' icon, implies selecting content to insert/attach
   * 'navigate' = shows a navigation arrow, used for quick jumps
   */
  mode = input<'attach' | 'navigate'>('attach');

  searchQuery = signal('');
  results = signal<any[]>([]);
  isLoading = signal(false);
  currentLang = computed(() => this.translate.currentLang || 'en');

  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private searchSubject = new Subject<string>();

  constructor() {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.isLoading.set(true)),
        switchMap((query) => {
          if (!query || query.length < 3) {
            return Promise.resolve({ results: [] });
          }
          return this.contentService.searchContent(query, 0, 10).toPromise();
        })
      )
      .subscribe({
        next: (response: any) => {
          this.isLoading.set(false);
          const validResults = (response?.results || []).filter((r: any) => (r.type === 'lecture' || r.type === 'note' || r.type === 'blog') && r.contentID);
          this.results.set(validResults);
        },
        error: () => {
          this.isLoading.set(false);
          this.results.set([]);
        },
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.searchInput()?.nativeElement.focus();
    }, 100);
  }

  onSearchChange(value: string) {
    this.searchQuery.set(value);
    if (!value || value.length < 3) {
      this.results.set([]);
      this.isLoading.set(false);
      return;
    }
    this.searchSubject.next(value);
  }

  getIconForType(type: string): string {
    switch (type) {
      case 'lecture':
        return 'school';
      case 'note':
        return 'sticky_note_2';
      case 'blog':
        return 'article';
      default:
        return 'description';
    }
  }

  selectItem(item: any) {
    const parentName = item.courseName ? ` (${item.courseName})` : '';
    const name = item.name?.[this.currentLang()] || item.name || 'Untitled';
    this.itemSelected.emit({
      id: item.contentID,
      title: `${name}${parentName}`,
      type: item.type,
      name,
    });
  }

  close() {
    this.closed.emit();
  }
}
